/**
 * A small, static emoji set for reactions and quick inserts — the everyday
 * ones, not the whole Unicode list (no dependency, no download). Each entry
 * carries German search words; the picker also matches the category name.
 */

export interface NxEmoji {
  /** The emoji itself. */
  e: string;
  /** Search words, lower case, space separated. */
  k: string;
}

export interface NxEmojiCategory {
  id: string;
  label: string;
  /** Emoji shown on the category tab. */
  icon: string;
  emojis: readonly NxEmoji[];
}

export const NX_QUICK_REACTIONS: readonly string[] = ['👍', '❤️', '😂', '😮', '🎉', '🔥'];

export const NX_EMOJI_CATEGORIES: readonly NxEmojiCategory[] = [
  {
    id: 'smileys',
    label: 'Smileys',
    icon: '😀',
    emojis: [
      { e: '😀', k: 'grinsen lachen froh' },
      { e: '😄', k: 'lachen freude' },
      { e: '😁', k: 'strahlen grinsen' },
      { e: '😂', k: 'tränen lachen lustig' },
      { e: '🤣', k: 'kugeln lachen' },
      { e: '😊', k: 'lächeln freundlich' },
      { e: '🙂', k: 'lächeln leicht' },
      { e: '😉', k: 'zwinkern' },
      { e: '😍', k: 'verliebt herzaugen' },
      { e: '🥰', k: 'verliebt herzen' },
      { e: '😘', k: 'kuss' },
      { e: '😎', k: 'cool sonnenbrille' },
      { e: '🤩', k: 'begeistert sterne' },
      { e: '🥳', k: 'party feiern' },
      { e: '🤔', k: 'nachdenken überlegen' },
      { e: '🤨', k: 'skeptisch' },
      { e: '😐', k: 'neutral' },
      { e: '🙄', k: 'augenrollen genervt' },
      { e: '😮', k: 'staunen überrascht' },
      { e: '😲', k: 'erstaunt schock' },
      { e: '😴', k: 'schlafen müde' },
      { e: '😅', k: 'erleichtert schwitzen' },
      { e: '😬', k: 'grimasse peinlich' },
      { e: '😢', k: 'traurig weinen' },
      { e: '😭', k: 'heulen weinen' },
      { e: '😤', k: 'schnauben stolz' },
      { e: '😡', k: 'wütend ärger' },
      { e: '🤯', k: 'kopf explodiert' },
      { e: '🥲', k: 'lächeln träne' },
      { e: '🤗', k: 'umarmen' },
      { e: '🫡', k: 'salutieren verstanden' },
      { e: '🤐', k: 'schweigen' },
    ],
  },
  {
    id: 'gestures',
    label: 'Gesten',
    icon: '👍',
    emojis: [
      { e: '👍', k: 'daumen hoch gut ja' },
      { e: '👎', k: 'daumen runter schlecht nein' },
      { e: '👏', k: 'klatschen applaus' },
      { e: '🙌', k: 'hände hoch feiern' },
      { e: '🙏', k: 'bitte danke beten' },
      { e: '🤝', k: 'handschlag deal' },
      { e: '👋', k: 'winken hallo' },
      { e: '✌️', k: 'frieden victory' },
      { e: '🤞', k: 'daumen drücken glück' },
      { e: '👌', k: 'ok perfekt' },
      { e: '🤙', k: 'anrufen' },
      { e: '💪', k: 'stark bizeps' },
      { e: '👀', k: 'augen schauen' },
      { e: '🧠', k: 'gehirn idee' },
      { e: '🫶', k: 'herzhände' },
      { e: '☝️', k: 'hinweis zeigefinger' },
    ],
  },
  {
    id: 'symbols',
    label: 'Herzen & Symbole',
    icon: '❤️',
    emojis: [
      { e: '❤️', k: 'herz liebe rot' },
      { e: '🧡', k: 'herz orange' },
      { e: '💛', k: 'herz gelb' },
      { e: '💚', k: 'herz grün' },
      { e: '💙', k: 'herz blau' },
      { e: '💜', k: 'herz lila' },
      { e: '🖤', k: 'herz schwarz' },
      { e: '💔', k: 'gebrochenes herz' },
      { e: '✅', k: 'erledigt haken ja' },
      { e: '❌', k: 'kreuz nein falsch' },
      { e: '⚠️', k: 'warnung achtung' },
      { e: '❗', k: 'ausrufezeichen wichtig' },
      { e: '❓', k: 'frage' },
      { e: '💯', k: 'hundert perfekt' },
      { e: '⭐', k: 'stern' },
      { e: '✨', k: 'glitzer neu' },
      { e: '🔥', k: 'feuer heiß' },
      { e: '⚡', k: 'blitz schnell' },
      { e: '💡', k: 'idee glühbirne' },
      { e: '📌', k: 'pin wichtig' },
    ],
  },
  {
    id: 'objects',
    label: 'Arbeit & Objekte',
    icon: '💼',
    emojis: [
      { e: '🎉', k: 'party konfetti feiern' },
      { e: '🚀', k: 'rakete start launch' },
      { e: '🏆', k: 'pokal gewinner' },
      { e: '🎯', k: 'ziel treffer' },
      { e: '📈', k: 'diagramm steigend wachstum' },
      { e: '📉', k: 'diagramm fallend' },
      { e: '📅', k: 'kalender termin' },
      { e: '⏰', k: 'wecker zeit' },
      { e: '📎', k: 'büroklammer anhang' },
      { e: '📝', k: 'notiz schreiben' },
      { e: '📧', k: 'mail email' },
      { e: '📞', k: 'telefon anruf' },
      { e: '💼', k: 'aktentasche arbeit' },
      { e: '🛠️', k: 'werkzeug reparieren' },
      { e: '🔒', k: 'schloss sicher' },
      { e: '🔑', k: 'schlüssel' },
      { e: '💰', k: 'geld' },
      { e: '🧾', k: 'rechnung beleg' },
      { e: '📦', k: 'paket lieferung' },
      { e: '🚚', k: 'lieferwagen versand' },
    ],
  },
  {
    id: 'nature',
    label: 'Natur & Essen',
    icon: '🌿',
    emojis: [
      { e: '☀️', k: 'sonne wetter' },
      { e: '🌧️', k: 'regen wetter' },
      { e: '❄️', k: 'schnee kalt' },
      { e: '🌈', k: 'regenbogen' },
      { e: '🌿', k: 'pflanze grün' },
      { e: '🌸', k: 'blume blüte' },
      { e: '🐶', k: 'hund' },
      { e: '🐱', k: 'katze' },
      { e: '☕', k: 'kaffee pause' },
      { e: '🍕', k: 'pizza essen' },
      { e: '🍰', k: 'kuchen geburtstag' },
      { e: '🍻', k: 'bier prost' },
    ],
  },
];
